import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { Phone, Mail, MessageCircle, LogOut, ChevronRight, ChevronLeft, ChevronDown, Check, User, Globe, Smartphone, AtSign, FileCheck, Building2, FileText, Calendar, MapPin, Camera, Upload, CreditCard, Home, Briefcase, Volume2, VolumeX, Menu, Search, Send, HelpCircle, Users, Star, Receipt, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { companyInfo } from '../mock/data';
import LanguageSelector from '../components/LanguageSelector';
import MobileMenu from '../components/MobileMenu';
import MobileBottomNav from '../components/MobileBottomNav';
import VisaStatusModal from '../components/VisaStatusModal';
import ToolSidebar from '../components/ToolSidebar';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import '../styles/BusinessPage.css';
import '../styles/HomePage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Voice guidance texts for each step in different languages
const voiceTexts = {
  en: {
    step1: "Please enter your company name.",
    step2: "Please enter your first name and last name.",
    step3: "Please select your nationality from the dropdown.",
    step4: "Please enter your mobile phone number.",
    step5: "Please enter your email address.",
    step6: "Please upload a clear photo of yourself.",
    step7: "Please upload a copy of your passport.",
    step8: "Would you like to include an investor visa? Check the box if yes. Then submit your application."
  },
  ru: {
    step1: "Пожалуйста, введите название вашей компании.",
    step2: "Пожалуйста, введите ваше имя и фамилию.",
    step3: "Пожалуйста, выберите вашу национальность из списка.",
    step4: "Пожалуйста, введите номер вашего мобильного телефона.",
    step5: "Пожалуйста, введите ваш адрес электронной почты.",
    step6: "Пожалуйста, загрузите четкую фотографию себя.",
    step7: "Пожалуйста, загрузите копию вашего паспорта.",
    step8: "Хотите ли вы включить инвесторскую визу? Отметьте галочкой, если да. Затем отправьте заявку."
  },
  uz: {
    step1: "Iltimos, kompaniyangiz nomini kiriting.",
    step2: "Iltimos, ismingiz va familiyangizni kiriting.",
    step3: "Iltimos, ro'yxatdan millatingizni tanlang.",
    step4: "Iltimos, mobil telefon raqamingizni kiriting.",
    step5: "Iltimos, elektron pochta manzilingizni kiriting.",
    step6: "Iltimos, o'zingizning aniq suratini yuklang.",
    step7: "Iltimos, pasportingiz nusxasini yuklang.",
    step8: "Investor vizasini qo'shishni xohlaysizmi? Ha bo'lsa, belgini qo'ying. Keyin arizangizni yuboring."
  }
};

// Business page translations
const businessTranslations = {
  en: {
    backToHome: "Back to Home",
    startYourBusiness: "Start Your Business",
    fillOutForm: "Fill out the form to get started",
    openCompany: "Open Company",
    companyName: "Company Name",
    enterCompanyName: "Enter your company name",
    personalInfo: "Personal Information",
    enterFullName: "Enter your full name",
    firstName: "First Name",
    lastName: "Last Name",
    nationality: "Nationality",
    selectNationality: "Select your nationality",
    contactNumber: "Contact Number",
    enterMobile: "Enter your mobile number",
    mobileNumber: "Mobile Number",
    emailAddress: "Email Address",
    enterEmail: "Enter your email for updates",
    email: "Email",
    yourPhoto: "Your Photo",
    uploadPhoto: "Upload a clear photo of yourself",
    clickToUpload: "Click to upload photo",
    clickToChange: "Click to change",
    passportCopy: "Passport Copy",
    uploadPassport: "Upload a clear copy of your passport",
    clickToUploadPassport: "Click to upload passport",
    visaOption: "Visa Option",
    includeVisa: "Would you like to include investor visa?",
    businessPackage: "Business Setup Package",
    investorVisa: "Investor Visa",
    total: "Total",
    wantVisa: "Do you want to get visa from this company?",
    back: "Back",
    next: "Next",
    submit: "Submit Application",
    submitting: "Submitting...",
    successTitle: "Application Submitted Successfully!",
    successMessage: "Your documents have been approved. Our team will contact you within 24 hours.",
    simpleProcess: "Simple 4-Step Process",
    consultation: "Consultation",
    consultationDesc: "Free consultation to understand your needs",
    documentation: "Documentation",
    documentationDesc: "We prepare all required documents",
    registration: "Registration",
    registrationDesc: "Company registration with authorities",
    handover: "Handover",
    handoverDesc: "Receive your license and start business",
    contactUs: "Contact Us",
    contactDesc: "Get in touch with our team",
    voiceOn: "Voice guidance on",
    voiceOff: "Voice guidance off",
    liveLicensePreview: "Live License Preview",
    investorIdPreview: "Investor Visa ID Preview",
    // SEO Hero
    seoTitle: "Start Business in UAE",
    seoDesc: "If you want to start a business in UAE, Registan Tours can help you with company setup in Dubai, trade license, investor visa, and free zone company registration.",
    // Visa Options
    businessVisaOptions: "Business Visa Options",
    mainlandSetup: "Mainland Company Setup",
    mainlandDesc: "Does not include investor visa; it must be applied separately. Emirates ID and residence visa are required for all company owners.",
    freezoneSetup: "Free Zone Company Setup",
    freezoneDesc: "Includes investor visa, Emirates ID, and residence visa.",
    // FAQ
    faqHeading: "Frequently Asked Questions",
    faqDocsTitle: "Documents Required",
    faqDocsText: "To start a business in UAE, you usually need a passport copy, photo, phone number and preferred company name.",
    faqCostTitle: "How Much Does It Cost?",
    faqCostText: "The cost to open a company in the UAE depends on the type of business and location. Free zone company setup usually starts from AED 10,000, including the investor visa. Mainland company setup typically starts from AED 15,000–20,000, not including the investor visa.",
    faqTimeTitle: "How Long Does It Take?",
    faqTimeText: "Business setup in Dubai normally takes 3 to 7 working days after submitting the documents.",
    faqWhyTitle: "Why Choose Registan Tours?",
    faqWhyText1: "Registan Tours",
    faqWhyText2: "helps clients with company registration, trade license, investor visa and all business setup documents in",
    faqWhyText3: "UAE business setup",
    faqForeignersTitle: "Can foreigners start a business in UAE?",
    faqForeignersText: "Yes, foreigners can open a company in UAE with 100% ownership in many free zones.",
    faqRemoteTitle: "Do I need to be in Dubai?",
    faqRemoteText: "No, some company setup options can be done remotely."
  },
  ru: {
    backToHome: "На главную",
    startYourBusiness: "Начните свой бизнес",
    fillOutForm: "Заполните форму для начала",
    openCompany: "Открыть компанию",
    companyName: "Название компании",
    enterCompanyName: "Введите название компании",
    personalInfo: "Личная информация",
    enterFullName: "Введите ваше полное имя",
    firstName: "Имя",
    lastName: "Фамилия",
    nationality: "Национальность",
    selectNationality: "Выберите национальность",
    contactNumber: "Контактный номер",
    enterMobile: "Введите номер телефона",
    mobileNumber: "Мобильный номер",
    emailAddress: "Электронная почта",
    enterEmail: "Введите email для обновлений",
    email: "Email",
    yourPhoto: "Ваше фото",
    uploadPhoto: "Загрузите четкую фотографию",
    clickToUpload: "Нажмите для загрузки",
    clickToChange: "Нажмите для изменения",
    passportCopy: "Копия паспорта",
    uploadPassport: "Загрузите копию паспорта",
    clickToUploadPassport: "Нажмите для загрузки паспорта",
    visaOption: "Опция визы",
    includeVisa: "Хотите включить инвесторскую визу?",
    businessPackage: "Пакет регистрации бизнеса",
    investorVisa: "Инвесторская виза",
    total: "Итого",
    wantVisa: "Хотите получить визу от этой компании?",
    back: "Назад",
    next: "Далее",
    submit: "Отправить заявку",
    submitting: "Отправка...",
    successTitle: "Заявка успешно отправлена!",
    successMessage: "Ваши документы одобрены. Наша команда свяжется с вами в течение 24 часов.",
    simpleProcess: "Простой 4-шаговый процесс",
    consultation: "Консультация",
    consultationDesc: "Бесплатная консультация для понимания ваших потребностей",
    documentation: "Документация",
    documentationDesc: "Мы подготовим все необходимые документы",
    registration: "Регистрация",
    registrationDesc: "Регистрация компании в органах",
    handover: "Передача",
    handoverDesc: "Получите лицензию и начните бизнес",
    contactUs: "Свяжитесь с нами",
    contactDesc: "Свяжитесь с нашей командой",
    voiceOn: "Голосовое сопровождение включено",
    voiceOff: "Голосовое сопровождение выключено",
    liveLicensePreview: "Предпросмотр лицензии",
    investorIdPreview: "Предпросмотр ID визы инвестора",
    // SEO Hero
    seoTitle: "Открыть бизнес в ОАЭ",
    seoDesc: "Если вы хотите открыть бизнес в ОАЭ, Registan Tours поможет вам с регистрацией компании в Дубае, торговой лицензией, инвесторской визой и регистрацией в свободной зоне.",
    // Visa Options
    businessVisaOptions: "Варианты бизнес-визы",
    mainlandSetup: "Регистрация на материке",
    mainlandDesc: "Не включает инвесторскую визу; её необходимо оформить отдельно. Emirates ID и резидентская виза обязательны для всех владельцев компании.",
    freezoneSetup: "Регистрация в свободной зоне",
    freezoneDesc: "Включает инвесторскую визу, Emirates ID и резидентскую визу.",
    // FAQ
    faqHeading: "Часто задаваемые вопросы",
    faqDocsTitle: "Необходимые документы",
    faqDocsText: "Для открытия бизнеса в ОАЭ обычно нужны копия паспорта, фото, номер телефона и желаемое название компании.",
    faqCostTitle: "Сколько это стоит?",
    faqCostText: "Стоимость открытия компании в ОАЭ зависит от типа бизнеса и местоположения. Регистрация в свободной зоне обычно начинается от 10 000 AED, включая инвесторскую визу. Регистрация на материке — от 15 000–20 000 AED, без инвесторской визы.",
    faqTimeTitle: "Сколько времени это занимает?",
    faqTimeText: "Регистрация бизнеса в Дубае обычно занимает от 3 до 7 рабочих дней после подачи документов.",
    faqWhyTitle: "Почему выбирают Registan Tours?",
    faqWhyText1: "Registan Tours",
    faqWhyText2: "помогает клиентам с регистрацией компании, торговой лицензией, инвесторской визой и всеми документами для открытия бизнеса в",
    faqWhyText3: "ОАЭ",
    faqForeignersTitle: "Могут ли иностранцы открыть бизнес в ОАЭ?",
    faqForeignersText: "Да, иностранцы могут открыть компанию в ОАЭ с 100% владением во многих свободных зонах.",
    faqRemoteTitle: "Нужно ли быть в Дубае?",
    faqRemoteText: "Нет, некоторые варианты регистрации компании можно оформить удалённо."
  },
  uz: {
    backToHome: "Bosh sahifaga",
    startYourBusiness: "Biznesingizni boshlang",
    fillOutForm: "Boshlash uchun shaklni to'ldiring",
    openCompany: "Kompaniya ochish",
    companyName: "Kompaniya nomi",
    enterCompanyName: "Kompaniya nomini kiriting",
    personalInfo: "Shaxsiy ma'lumotlar",
    enterFullName: "To'liq ismingizni kiriting",
    firstName: "Ism",
    lastName: "Familiya",
    nationality: "Millat",
    selectNationality: "Millatingizni tanlang",
    contactNumber: "Aloqa raqami",
    enterMobile: "Telefon raqamingizni kiriting",
    mobileNumber: "Mobil raqam",
    emailAddress: "Elektron pochta",
    enterEmail: "Yangiliklar uchun email kiriting",
    email: "Email",
    yourPhoto: "Sizning suratiz",
    uploadPhoto: "Aniq suratni yuklang",
    clickToUpload: "Yuklash uchun bosing",
    clickToChange: "O'zgartirish uchun bosing",
    passportCopy: "Pasport nusxasi",
    uploadPassport: "Pasport nusxasini yuklang",
    clickToUploadPassport: "Pasportni yuklash uchun bosing",
    visaOption: "Viza varianti",
    includeVisa: "Investor vizasini qo'shishni xohlaysizmi?",
    businessPackage: "Biznes ro'yxatga olish paketi",
    investorVisa: "Investor vizasi",
    total: "Jami",
    wantVisa: "Bu kompaniyadan viza olishni xohlaysizmi?",
    back: "Orqaga",
    next: "Keyingi",
    submit: "Arizani yuborish",
    submitting: "Yuborilmoqda...",
    successTitle: "Ariza muvaffaqiyatli yuborildi!",
    successMessage: "Hujjatlaringiz tasdiqlandi. Jamoamiz 24 soat ichida siz bilan bog'lanadi.",
    simpleProcess: "Oddiy 4-bosqichli jarayon",
    consultation: "Maslahat",
    consultationDesc: "Ehtiyojlaringizni tushunish uchun bepul maslahat",
    documentation: "Hujjatlashtirish",
    documentationDesc: "Barcha kerakli hujjatlarni tayyorlaymiz",
    registration: "Ro'yxatga olish",
    registrationDesc: "Kompaniyani rasmiy ro'yxatga olish",
    handover: "Topshirish",
    handoverDesc: "Litsenziyani oling va biznesni boshlang",
    contactUs: "Biz bilan bog'laning",
    contactDesc: "Jamoamiz bilan bog'laning",
    voiceOn: "Ovozli yo'riqnoma yoqildi",
    voiceOff: "Ovozli yo'riqnoma o'chirildi",
    liveLicensePreview: "Litsenziya oldindan ko'rish",
    investorIdPreview: "Investor ID oldindan ko'rish",
    // SEO Hero
    seoTitle: "BAAda biznes boshlash",
    seoDesc: "Agar siz BAAda biznes boshlashni xohlasangiz, Registan Tours Dubayda kompaniya ochish, savdo litsenziyasi, investor vizasi va erkin zonada ro'yxatga olishda yordam beradi.",
    // Visa Options
    businessVisaOptions: "Biznes viza variantlari",
    mainlandSetup: "Materikda kompaniya ochish",
    mainlandDesc: "Investor vizasini o'z ichiga olmaydi; uni alohida rasmiylashtirish kerak. Emirates ID va rezidentlik vizasi barcha kompaniya egalari uchun talab qilinadi.",
    freezoneSetup: "Erkin zonada kompaniya ochish",
    freezoneDesc: "Investor vizasi, Emirates ID va rezidentlik vizasini o'z ichiga oladi.",
    // FAQ
    faqHeading: "Ko'p so'raladigan savollar",
    faqDocsTitle: "Kerakli hujjatlar",
    faqDocsText: "BAAda biznes ochish uchun odatda pasport nusxasi, surat, telefon raqami va kompaniya nomi kerak bo'ladi.",
    faqCostTitle: "Bu qancha turadi?",
    faqCostText: "BAAda kompaniya ochish narxi biznes turi va joylashuviga bog'liq. Erkin zonada ro'yxatga olish odatda 10 000 AED dan boshlanadi, investor vizasi bilan. Materikda ro'yxatga olish 15 000–20 000 AED dan boshlanadi, investor vizasisiz.",
    faqTimeTitle: "Bu qancha vaqt oladi?",
    faqTimeText: "Dubayda biznes ro'yxatga olish hujjatlar topshirilgandan so'ng odatda 3 dan 7 ish kunigacha davom etadi.",
    faqWhyTitle: "Nima uchun Registan Tours?",
    faqWhyText1: "Registan Tours",
    faqWhyText2: "kompaniya ro'yxatga olish, savdo litsenziyasi, investor vizasi va barcha biznes hujjatlari bilan mijozlarga yordam beradi",
    faqWhyText3: "BAAda biznes ochish",
    faqForeignersTitle: "Chet elliklar BAAda biznes ocha oladimi?",
    faqForeignersText: "Ha, chet elliklar ko'plab erkin zonalarda 100% egalik bilan kompaniya ocha oladi.",
    faqRemoteTitle: "Dubayda bo'lishim kerakmi?",
    faqRemoteText: "Yo'q, ba'zi kompaniya ochish variantlarini masofadan amalga oshirish mumkin."
  }
};

// Live License Preview Component
const LicensePreview = ({ formData, isStamped = false }) => {
  const fullName = `${formData.firstName} ${formData.lastName}`.trim() || 'Your Name';
  const companyName = formData.companyName || 'Your Company';
  const licenseNumber = `DXB-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  const issueDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const expiryDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  
  return (
    <div className="license-preview-container" data-testid="license-preview">
      <Helmet>
        <link rel="canonical" href="https://registantours.com/start-business" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/start-business" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="license-preview-header">
        <FileText size={20} />
        <span>Live License Preview</span>
      </div>
      
      <div className={`license-card ${isStamped ? 'stamped' : ''}`}>
        {/* Stamp Overlay */}
        {isStamped && (
          <div className="stamp-overlay">
            <div className="stamp-approved">
              <span className="stamp-text">APPROVED</span>
              <span className="stamp-date">{issueDate}</span>
            </div>
          </div>
        )}
        
        {/* License Header */}
        <div className="license-top-header">
          <div className="license-logo-section">
            <div className="dubai-emblem">
              <Building2 size={32} />
            </div>
            <div className="license-title-text">
              <span className="arabic-title">رخصة تجارية</span>
              <span className="english-title">Commercial License</span>
            </div>
          </div>
          <div className="license-qr-placeholder">
            <div className="qr-code-mock"></div>
          </div>
        </div>
        
        {/* License Details Section */}
        <div className="license-section">
          <div className="section-header">
            <span className="section-title-en">License Details</span>
            <span className="section-title-ar">تفاصيل الرخصة</span>
          </div>
          
          <div className="license-details-grid">
            <div className="detail-row">
              <span className="detail-label">License No.</span>
              <span className="detail-value">{licenseNumber}</span>
              <span className="detail-label-ar">رقم الرخصة</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Company Name</span>
              <span className="detail-value highlight">{companyName || '---'}</span>
              <span className="detail-label-ar">اسم الشركة</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Trade Name</span>
              <span className="detail-value highlight">{fullName || '---'}</span>
              <span className="detail-label-ar">الإسم التجاري</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Legal Type</span>
              <span className="detail-value">Commercial License</span>
              <span className="detail-label-ar">الشكل القانوني</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Issue Date</span>
              <span className="detail-value">{issueDate}</span>
              <span className="detail-label-ar">تاريخ الإصدار</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Expiry Date</span>
              <span className="detail-value">{expiryDate}</span>
              <span className="detail-label-ar">تاريخ الإنتهاء</span>
            </div>
          </div>
        </div>
        
        {/* License Members Section */}
        <div className="license-section">
          <div className="section-header">
            <span className="section-title-en">License Members</span>
            <span className="section-title-ar">أعضاء الرخصة</span>
          </div>
          
          <div className="license-details-grid">
            <div className="detail-row">
              <span className="detail-label">Name</span>
              <span className="detail-value highlight">{fullName || '---'}</span>
              <span className="detail-label-ar">الإسم</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Nationality</span>
              <span className="detail-value highlight">{formData.nationality || '---'}</span>
              <span className="detail-label-ar">الجنسية</span>
            </div>
          </div>
        </div>
        
        {/* License Activities Section */}
        <div className="license-section">
          <div className="section-header">
            <span className="section-title-en">License Activities</span>
            <span className="section-title-ar">نشاط الرخصة التجارية</span>
          </div>
          
          <div className="activities-list">
            <div className="activity-item">General Trading</div>
            <div className="activity-item">Import & Export</div>
          </div>
        </div>
        
        {/* Contact Information Section */}
        <div className="license-section">
          <div className="section-header">
            <span className="section-title-en">Contact Information</span>
            <span className="section-title-ar">معلومات الإتصال</span>
          </div>
          
          <div className="license-details-grid">
            <div className="detail-row">
              <span className="detail-label">Mobile No</span>
              <span className="detail-value highlight">{formData.mobile || '---'}</span>
              <span className="detail-label-ar">هاتف متحرك</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Email</span>
              <span className="detail-value highlight email-value">{formData.email || '---'}</span>
              <span className="detail-label-ar">البريد الإلكتروني</span>
            </div>
          </div>
        </div>
        
        {/* Address Section */}
        <div className="license-section address-section">
          <div className="section-header">
            <span className="section-title-en">Address</span>
            <span className="section-title-ar">العنوان</span>
          </div>
          
          <div className="address-content">
            <MapPin size={14} />
            <span>Dubai, United Arab Emirates</span>
          </div>
        </div>
        
        {/* Footer */}
        <div className="license-footer">
          <div className="footer-seal">
            <div className="seal-circle">
              <Building2 size={16} />
            </div>
          </div>
          <div className="footer-text">
            <span>Department of Economic Development - Dubai</span>
          </div>
        </div>
      </div>
      
      <div className="license-preview-note">
        <Calendar size={14} />
        <span>This is a preview. Your actual license will be issued after approval.</span>
      </div>
    </div>
  );
};

// UAE Investor Visa ID Card Preview Component
const VisaIdCardPreview = ({ formData, show, isStamped = false }) => {
  const fullName = `${formData.firstName} ${formData.lastName}`.trim() || 'Your Name';
  const idNumber = `784-${new Date().getFullYear()}-${Math.random().toString().substring(2, 9)}-1`;
  const issueDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const expiryDate = new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  
  return (
    <div className={`visa-id-card-container ${show ? 'show' : ''}`} data-testid="visa-id-card">
      <div className="visa-id-card-header">
        <CreditCard size={18} />
        <span>Investor Visa ID Preview</span>
      </div>
      
      <div className={`visa-id-card ${isStamped ? 'stamped' : ''}`}>
        {/* Stamp Overlay for ID Card */}
        {isStamped && (
          <div className="id-stamp-overlay">
            <div className="id-stamp-approved">
              <span className="id-stamp-text">VERIFIED</span>
            </div>
          </div>
        )}
        
        {/* Card Header */}
        <div className="id-card-top">
          <div className="id-card-country">
            <span className="country-ar">الإمارات العربية المتحدة</span>
            <span className="country-en">UNITED ARAB EMIRATES</span>
          </div>
          <div className="id-card-type">
            <span className="type-ar">هوية المستثمر</span>
            <span className="type-en">INVESTOR ID</span>
          </div>
        </div>
        
        {/* Card Body */}
        <div className="id-card-body">
          {/* Photo Section */}
          <div className="id-card-photo-section">
            {formData.photoPreview ? (
              <img src={formData.photoPreview} alt="ID Photo" className="id-card-photo" />
            ) : (
              <div className="id-card-photo-placeholder">
                <User size={40} />
              </div>
            )}
          </div>
          
          {/* Details Section */}
          <div className="id-card-details">
            <div className="id-detail-row">
              <span className="id-detail-label">Name / الاسم</span>
              <span className="id-detail-value">{fullName}</span>
            </div>
            
            <div className="id-detail-row">
              <span className="id-detail-label">Nationality / الجنسية</span>
              <span className="id-detail-value">{formData.nationality || '---'}</span>
            </div>
            
            <div className="id-detail-row">
              <span className="id-detail-label">ID Number / رقم الهوية</span>
              <span className="id-detail-value id-number">{idNumber}</span>
            </div>
            
            <div className="id-detail-dates">
              <div className="id-date-item">
                <span className="id-date-label">Issue / الإصدار</span>
                <span className="id-date-value">{issueDate}</span>
              </div>
              <div className="id-date-item">
                <span className="id-date-label">Expiry / الانتهاء</span>
                <span className="id-date-value">{expiryDate}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Card Footer */}
        <div className="id-card-footer">
          <div className="id-card-chip"></div>
          <div className="id-card-barcode"></div>
        </div>
      </div>
    </div>
  );
};

const BusinessPage = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const seo = useSEO('start-business', {
    title: 'Open Company in Dubai | Free Zone & Mainland Business Setup — Registan Tourism',
    description: 'Register a company in Dubai from AED 10,000. Free zone and mainland setup with trade license, investor visa, and Emirates ID. 100% foreign ownership.',
    keywords: 'open company dubai, register company UAE, free zone company dubai, mainland company setup, trade license dubai, investor visa UAE'
  });
  const currentLogo = theme === 'light' 
    ? "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png"
    : "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const passportInputRef = useRef(null);
  
  // Get translations based on current language
  const bt = businessTranslations[language] || businessTranslations.en;
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Visa status modal state
  const [isVisaStatusOpen, setIsVisaStatusOpen] = useState(false);
  
  // Voice state
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    nationality: '',
    mobile: '',
    email: '',
    photo: null,
    photoPreview: null,
    passport: null,
    passportPreview: null,
    wantVisa: false,
    zone: '' // 'freezone' | 'mainland' | '' (set via ?zone= query param)
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showStampAnimation, setShowStampAnimation] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [visaOptionOpen, setVisaOptionOpen] = useState(null); // 'freezone' | 'mainland' | null

  // Auto-start form + pre-select zone when ?zone=freezone|mainland is in URL
  // (e.g., user clicked Book Now on the Business card from the homepage)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const zone = params.get('zone');
      if (zone === 'freezone' || zone === 'mainland') {
        // Pre-fill zone. For Free Zone, wantVisa defaults to true (package includes investor visa).
        setFormData((prev) => ({
          ...prev,
          zone,
          wantVisa: zone === 'freezone' ? true : prev.wantVisa,
        }));
        // Show the zone comparison accordion for context
        setVisaOptionOpen(zone);
        // Immediately start the 8-step application form
        setShowForm(true);
        setVoiceEnabled(true);
        setCurrentStep(1);
        setTimeout(() => {
          const el = document.querySelector('.business-form-container, .form-step, .form-container');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSteps = 8;
  const basePrice = 16000;
  const visaPrice = 4000;
  const totalPrice = formData.wantVisa ? basePrice + visaPrice : basePrice;

  // Handle open company button click
  const handleOpenCompany = () => {
    setShowForm(true);
    // Enable voice when form opens
    setVoiceEnabled(true);
  };

  // Voice guidance function using OpenAI TTS API for all languages
  const speakText = useCallback(async (text) => {
    if (!voiceEnabled) return;
    
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    setIsSpeaking(true);
    
    try {
      const response = await fetch(`${API_URL}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, lang: language })
      });
      
      if (!response.ok) {
        throw new Error('TTS request failed');
      }
      
      const data = await response.json();
      
      // Create audio from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Play the audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };
      
      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
    }
  }, [voiceEnabled, language]);

  // Speak when step changes
  useEffect(() => {
    if (voiceEnabled) {
      const texts = voiceTexts[language] || voiceTexts.en;
      const stepKey = `step${currentStep}`;
      if (texts[stepKey]) {
        // Small delay for smoother UX
        setTimeout(() => {
          speakText(texts[stepKey]);
        }, 100);
      }
    }
    
    return () => {
      // Stop audio when component unmounts or step changes
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [currentStep, voiceEnabled, language, speakText]);

  // Toggle voice
  const toggleVoice = () => {
    if (voiceEnabled) {
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsSpeaking(false);
    } else {
      // Speak the current step when enabling voice
      const texts = voiceTexts[language] || voiceTexts.en;
      const stepKey = `step${currentStep}`;
      if (texts[stepKey]) {
        setTimeout(() => {
          speakText(texts[stepKey]);
        }, 200);
      }
    }
    setVoiceEnabled(!voiceEnabled);
  };

  const nationalities = [
    "Afghanistan", "Albania", "Algeria", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahrain", "Bangladesh", "Belarus", "Belgium", "Brazil", "Bulgaria", "Canada", "China", "Colombia",
    "Czech Republic", "Denmark", "Egypt", "Ethiopia", "Finland", "France", "Georgia", "Germany", "Ghana",
    "Greece", "Hungary", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Japan",
    "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan", "Lebanon", "Libya", "Malaysia", "Mexico",
    "Morocco", "Nepal", "Netherlands", "New Zealand", "Nigeria", "Norway", "Oman", "Pakistan", "Palestine",
    "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia", "Senegal", "Serbia",
    "Singapore", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland",
    "Syria", "Tajikistan", "Thailand", "Tunisia", "Turkey", "Turkmenistan", "UAE", "Uganda", "Ukraine",
    "United Kingdom", "United States", "Uzbekistan", "Vietnam", "Yemen"
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [fileType]: file,
          [`${fileType}Preview`]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.companyName.trim() !== '';
      case 2:
        return formData.firstName.trim() !== '' && formData.lastName.trim() !== '';
      case 3:
        return formData.nationality !== '';
      case 4:
        return formData.mobile.trim() !== '' && formData.mobile.length >= 8;
      case 5:
        return formData.email.trim() !== '' && formData.email.includes('@');
      case 6:
        return true; // Photo is optional
      case 7:
        return true; // Passport is optional
      case 8:
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep() && currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Use FormData to send files along with form data
      const submitData = new FormData();
      submitData.append('companyName', formData.companyName);
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('nationality', formData.nationality);
      submitData.append('mobile', formData.mobile);
      submitData.append('email', formData.email);
      submitData.append('includesVisa', formData.wantVisa);
      if (formData.zone) submitData.append('zone', formData.zone);
      submitData.append('totalPrice', `AED ${totalPrice.toLocaleString()}`);
      
      // Append files if they exist
      if (formData.photo) {
        submitData.append('photo', formData.photo);
      }
      if (formData.passport) {
        submitData.append('passport', formData.passport);
      }

      const response = await fetch(`${API_URL}/api/business-applications`, {
        method: 'POST',
        body: submitData
      });

      if (response.ok) {
        const data = await response.json();
        setSubmitSuccess(true);
        // Trigger stamp animation after a short delay
        setTimeout(() => {
          setShowStampAnimation(true);
        }, 500);
        // Reset form after success
        setTimeout(() => {
          setFormData({
            companyName: '',
            firstName: '',
            lastName: '',
            nationality: '',
            mobile: '',
            email: '',
            photo: null,
            photoPreview: null,
            passport: null,
            passportPreview: null,
            wantVisa: false
          });
          setCurrentStep(1);
          setSubmitSuccess(false);
          setShowStampAnimation(false);
        }, 8000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmitError(errorData.detail || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-icon"><Briefcase size={32} /></div>
            <h3>{bt.companyName}</h3>
            <p>{bt.enterCompanyName}</p>
            <div className="form-group">
              <label>{bt.companyName} *</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder={bt.enterCompanyName}
                required
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="step-content">
            <div className="step-icon"><User size={32} /></div>
            <h3>{bt.personalInfo}</h3>
            <p>{bt.enterFullName}</p>
            <div className="form-row">
              <div className="form-group">
                <label>{bt.firstName} *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder={bt.firstName}
                  required
                />
              </div>
              <div className="form-group">
                <label>{bt.lastName} *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder={bt.lastName}
                  required
                />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="step-content">
            <div className="step-icon"><Globe size={32} /></div>
            <h3>{bt.nationality}</h3>
            <p>{bt.selectNationality}</p>
            <div className="form-group">
              <label>{bt.nationality} *</label>
              <select
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                required
              >
                <option value="">{bt.selectNationality}</option>
                {nationalities.map(nat => (
                  <option key={nat} value={nat}>{nat}</option>
                ))}
              </select>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="step-content">
            <div className="step-icon"><Smartphone size={32} /></div>
            <h3>{bt.contactNumber}</h3>
            <p>{bt.enterMobile}</p>
            <div className="form-group">
              <label>{bt.mobileNumber} *</label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="+971 50 123 4567"
                required
              />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="step-content">
            <div className="step-icon"><AtSign size={32} /></div>
            <h3>{bt.emailAddress}</h3>
            <p>{bt.enterEmail}</p>
            <div className="form-group">
              <label>{bt.email} *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>
        );
      case 6:
        return (
          <div className="step-content">
            <div className="step-icon"><Camera size={32} /></div>
            <h3>{bt.yourPhoto}</h3>
            <p>{bt.uploadPhoto}</p>
            
            <div className="upload-area" onClick={() => photoInputRef.current?.click()}>
              {formData.photoPreview ? (
                <div className="upload-preview">
                  <img src={formData.photoPreview} alt="Preview" />
                  <span className="change-text">{bt.clickToChange}</span>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <Upload size={48} />
                  <span>{bt.clickToUpload}</span>
                  <span className="upload-hint">JPG, PNG (Max 5MB)</span>
                </div>
              )}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'photo')}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        );
      case 7:
        return (
          <div className="step-content">
            <div className="step-icon"><FileText size={32} /></div>
            <h3>{bt.passportCopy}</h3>
            <p>{bt.uploadPassport}</p>
            
            <div className="upload-area" onClick={() => passportInputRef.current?.click()}>
              {formData.passportPreview ? (
                <div className="upload-preview passport-preview">
                  <img src={formData.passportPreview} alt="Passport Preview" />
                  <span className="change-text">{bt.clickToChange}</span>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <Upload size={48} />
                  <span>{bt.clickToUploadPassport}</span>
                  <span className="upload-hint">JPG, PNG, PDF (Max 5MB)</span>
                </div>
              )}
              <input
                ref={passportInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange(e, 'passport')}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        );
      case 8:
        return (
          <div className="step-content step-content-visa">
            <div className="step-icon"><FileCheck size={32} /></div>
            <h3>{bt.visaOption}</h3>
            <p>{bt.includeVisa}</p>
            
            <div className="price-display">
              <div className="price-row">
                <span>{bt.businessPackage}</span>
                <span className="price-value">AED 16,000</span>
              </div>
              {formData.wantVisa && (
                <div className="price-row visa-addon">
                  <span>+ {bt.investorVisa}</span>
                  <span className="price-value">AED 4,000</span>
                </div>
              )}
              <div className="price-row total">
                <span>{bt.total}</span>
                <span className="price-value total-price">AED {totalPrice.toLocaleString()}</span>
              </div>
            </div>
            
            <div className="visa-option">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  name="wantVisa"
                  checked={formData.wantVisa}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                <span className="checkbox-label">
                  {bt.wantVisa}
                  <span className="addon-price">(+AED 4,000)</span>
                </span>
              </label>
            </div>
            
            {/* ID Card Preview - slides in when visa is checked */}
            <VisaIdCardPreview formData={formData} show={formData.wantVisa} />
          </div>
        );
      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <div className="business-page">
        <header className="business-header">
          <div className="header-left">
            <Link to="/" className="header-logo-link">
              <img 
                src={currentLogo} 
                alt="Registan" 
                className="header-logo-img" 
              />
            </Link>
            <LanguageSelector />
          </div>
          <div className="header-right">
            {isAuthenticated ? (
              <div className="header-user-menu">
                <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
                  <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
                  <span>{user?.name}</span>
                </button>
              </div>
            ) : null}
          </div>
        </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/start-business" />
        
        <div className="success-container-stamps">
          <div className="success-header-text">
            <div className={`success-checkmark ${showStampAnimation ? 'show' : ''}`}>
              <Check size={48} />
            </div>
            <h2>{bt.successTitle}</h2>
            <p>{bt.successMessage}</p>
          </div>
          
          <div className="stamps-preview-grid">
            {/* License with Stamp */}
            <div className="stamp-card-wrapper">
              <LicensePreview formData={formData} isStamped={showStampAnimation} />
            </div>
            
            {/* ID Card with Stamp (only if visa selected) */}
            {formData.wantVisa && (
              <div className="stamp-card-wrapper id-card-stamped">
                <VisaIdCardPreview formData={formData} show={true} isStamped={showStampAnimation} />
              </div>
            )}
          </div>
          
          <Link to="/" className="btn-back-home">{bt.backToHome}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="business-page">
      {/* Header */}
      <header className="business-header">
        <div className="header-left">
          <Link to="/" className="header-logo-link">
            <img 
              src={currentLogo} 
              alt="Registan" 
              className="header-logo-img" 
            />
          </Link>
          <LanguageSelector />
          <button 
            className="visa-status-btn"
            onClick={() => setIsVisaStatusOpen(true)}
            data-testid="visa-status-btn"
          >
            <Search size={16} />
            <span>{t('visaStatus') || 'Visa Status'}</span>
          </button>
          <button 
            className="my-transactions-btn"
            onClick={() => navigate('/dashboard')}
            data-testid="my-transactions-btn"
          >
            <Receipt size={16} />
            <span>{t('myTransactions') || 'My Transactions'}</span>
          </button>
        </div>
        
        <nav className="business-nav desktop-hide-nav">
          <Link to="/" className="nav-link">{t('home') || 'Home'}</Link>
          <Link to="/start-business" className="nav-link active">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/tourismsoftware" className="nav-link">Tourism Software</Link>
          <Link to="/about" className="nav-link">{t('about') || 'About'}</Link>
        </nav>
        
        <div className="header-right">
          {isAuthenticated ? (
            <div className="header-user-menu">
              <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
                <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
                <span>{user?.name}</span>
              </button>
              <button className="btn-logout-header" onClick={logout} title="Logout">
                <LogOut size={20} />
              </button>
            </div>
          ) : (
            <div className="header-auth-buttons">
              <button className="btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
              <button className="btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'Sign Up'}</button>
            </div>
          )}
        </div>
        
        {/* Mobile Hamburger Button */}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      
      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        activeSection=""
        onNavigate={() => {}}
      />

      <div className="tool-page-layout">
        <ToolSidebar />
        <div className="tool-page-content">

      {/* Hero Section */}
      <section className="business-hero-with-form">
        {!showForm ? (
          /* Landing View - Before clicking Open Company */
          <div className="business-landing">
            <div className="landing-content">
              <h1 className="seo-hero-title">{bt.seoTitle}</h1>
              <p className="seo-hero-desc">{bt.seoDesc}</p>
              
              <button 
                className="btn-open-company"
                onClick={handleOpenCompany}
                data-testid="open-company-btn"
              >
                <Building2 size={24} />
                <span>{bt.openCompany || 'Open Company'}</span>
              </button>
              
              <div className="landing-features">
                <div className="landing-feature">
                  <Check size={20} />
                  <span>100% Foreign Ownership</span>
                </div>
                <div className="landing-feature">
                  <Check size={20} />
                  <span>5-10 Days Setup</span>
                </div>
                <div className="landing-feature">
                  <Check size={20} />
                  <span>0% Corporate Tax*</span>
                </div>
              </div>
            </div>
            
            {/* License Preview */}
            <div className="license-preview-section">
              <LicensePreview formData={formData} />
            </div>
          </div>
        ) : (
          /* Form View - After clicking Open Company */
          <div className="hero-form-container">
            {/* Left side - Form */}
            <div className="form-section">
              <div className="form-card">
                <div className="form-header">
                  <p>{bt.fillOutForm}</p>
                  
                  {/* Voice Toggle in Form Header */}
                  <button 
                    className={`btn-voice-toggle-inline ${voiceEnabled ? 'active' : ''} ${isSpeaking ? 'speaking' : ''}`}
                    onClick={toggleVoice}
                    title={voiceEnabled ? bt.voiceOff : bt.voiceOn}
                  >
                    {voiceEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                    <span>{voiceEnabled ? bt.voiceOn : bt.voiceOff}</span>
                  </button>
                </div>
                
                {/* Step indicators */}
                <div className="step-indicators">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(step => (
                    <div 
                      key={step} 
                      className={`step-indicator ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
                    >
                      {currentStep > step ? <Check size={16} /> : step}
                    </div>
                  ))}
                </div>
                
                {/* Form content */}
                <div className="form-body">
                  {renderStepContent()}
                  
                  {submitError && <div className="error-message">{submitError}</div>}
                
                {/* Navigation buttons */}
                <div className="form-navigation">
                  {currentStep > 1 && (
                    <button className="btn-prev" onClick={prevStep}>
                      <ChevronLeft size={20} />
                      {bt.back}
                    </button>
                  )}
                  
                  {currentStep < totalSteps ? (
                    <button 
                      className="btn-next" 
                      onClick={nextStep}
                      disabled={!validateStep()}
                    >
                      {bt.next}
                      <ChevronRight size={20} />
                    </button>
                  ) : (
                    <button 
                      className="btn-submit" 
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? bt.submitting : bt.submit}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side - License Preview */}
          <div className="license-preview-section">
            <LicensePreview formData={formData} />
          </div>
        </div>
        )}
      </section>

      {/* Process Steps */}
      <section className="process-section">
        <h2>{bt.simpleProcess}</h2>
        <div className="process-steps">
          <div className="process-step">
            <div className="step-number">1</div>
            <h3>{bt.consultation}</h3>
            <p>{bt.consultationDesc}</p>
          </div>
          <div className="process-connector"></div>
          <div className="process-step">
            <div className="step-number">2</div>
            <h3>{bt.documentation}</h3>
            <p>{bt.documentationDesc}</p>
          </div>
          <div className="process-connector"></div>
          <div className="process-step">
            <div className="step-number">3</div>
            <h3>{bt.registration}</h3>
            <p>{bt.registrationDesc}</p>
          </div>
          <div className="process-connector"></div>
          <div className="process-step">
            <div className="step-number">4</div>
            <h3>{bt.handover}</h3>
            <p>{bt.handoverDesc}</p>
          </div>
        </div>
      </section>

      {/* Business Visa Options */}
      <section className="visa-options-section" data-testid="visa-options">
        <h2 className="visa-options-heading">{bt.businessVisaOptions}</h2>
        <div className="visa-options-grid">

          <div className={`visa-option-card ${visaOptionOpen === 'mainland' ? 'open' : ''}`}
            onClick={() => setVisaOptionOpen(visaOptionOpen === 'mainland' ? null : 'mainland')}
            data-testid="mainland-card">
            <div className="visa-option-header">
              <Building2 size={22} />
              <h3>{bt.mainlandSetup}</h3>
              <ChevronDown size={20} className={`visa-option-chevron ${visaOptionOpen === 'mainland' ? 'rotated' : ''}`} />
            </div>
            {visaOptionOpen === 'mainland' && (
              <div className="visa-option-body">
                <p>{bt.mainlandDesc}</p>
              </div>
            )}
          </div>

          <div className={`visa-option-card ${visaOptionOpen === 'freezone' ? 'open' : ''}`}
            onClick={() => setVisaOptionOpen(visaOptionOpen === 'freezone' ? null : 'freezone')}
            data-testid="freezone-card">
            <div className="visa-option-header">
              <Globe size={22} />
              <h3>{bt.freezoneSetup}</h3>
              <ChevronDown size={20} className={`visa-option-chevron ${visaOptionOpen === 'freezone' ? 'rotated' : ''}`} />
            </div>
            {visaOptionOpen === 'freezone' && (
              <div className="visa-option-body">
                <p>{bt.freezoneDesc}</p>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* SEO FAQ Section */}
      <section className="business-faq-section" data-testid="business-faq">
        <h2 className="business-faq-heading">{bt.faqHeading}</h2>
        <div className="business-faq-list" itemScope itemType="https://schema.org/FAQPage">

          <div className="business-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{bt.faqDocsTitle}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">{bt.faqDocsText}</p>
            </div>
          </div>

          <div className="business-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{bt.faqCostTitle}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">{bt.faqCostText}</p>
            </div>
          </div>

          <div className="business-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{bt.faqTimeTitle}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">{bt.faqTimeText}</p>
            </div>
          </div>

          <div className="business-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{bt.faqWhyTitle}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text"><a href="https://registantours.com/post" target="_blank" rel="noopener noreferrer">{bt.faqWhyText1}</a> {bt.faqWhyText2} <a href="https://registantours.com/start-business" target="_blank" rel="noopener noreferrer">{bt.faqWhyText3}</a>.</p>
            </div>
          </div>

          <div className="business-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{bt.faqForeignersTitle}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">{bt.faqForeignersText}</p>
            </div>
          </div>

          <div className="business-faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <h3 itemProp="name">{bt.faqRemoteTitle}</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text">{bt.faqRemoteText}</p>
            </div>
          </div>

        </div>
      </section>

      {/* Contact Section */}
      <section className="business-contact-section">
        <div className="contact-content">
          <h2>{bt.contactUs}</h2>
          <p>{bt.contactDesc}</p>
          <div className="contact-buttons">
            <a 
              href="https://wa.me/971569130707?text=Hello!%20I'm%20interested%20in%20starting%20a%20business%20in%20Dubai."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <MessageCircle size={20} />
              <span>WhatsApp</span>
            </a>
            <a href="tel:+971569130707" className="btn-call">
              <Phone size={20} />
              <span>+971 56 913 0707</span>
            </a>
            <a href="mailto:support@registantours.com" className="btn-email">
              <Mail size={20} />
              <span>Email</span>
            </a>
          </div>
        </div>
      </section>
        </div>
      </div>

      {/* Visa Status Modal */}
      {isVisaStatusOpen && (
        <VisaStatusModal 
          onClose={() => setIsVisaStatusOpen(false)}
        />
      )}

      {/* Full Width Footer */}
      <footer id="contact" className="site-footer">
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-brand">
              <h2>Registan Tourism</h2>
              <p className="footer-tagline">{t('yourTrustedPartner')}</p>
            </div>
            <div className="footer-contact-info">
              <a href={`tel:${companyInfo.phone}`} className="footer-contact-item">
                <Phone size={18} />
                <span>{companyInfo.phone}</span>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="footer-contact-item">
                <Mail size={18} />
                <span>{companyInfo.email}</span>
              </a>
              <div className="footer-contact-item">
                <MapPin size={18} />
                <span>Dubai, United Arab Emirates</span>
              </div>
            </div>
            <a 
              href="https://t.me/changeVISAoman" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-telegram-btn"
            >
              <Send size={18} />
              {t('joinTelegram')}
            </a>
            <button 
              onClick={() => navigate('/faq')}
              className="footer-faq-btn"
              data-testid="faq-btn"
            >
              <HelpCircle size={18} />
              {t('faq') || 'FAQ'}
            </button>
            <button 
              onClick={() => navigate('/team')}
              className="footer-nav-btn"
              data-testid="footer-team-btn"
            >
              <Users size={18} />
              {t('team') || 'Team'}
            </button>
            <button 
              onClick={() => navigate('/#reviews')}
              className="footer-nav-btn"
              data-testid="footer-reviews-btn"
            >
              <Star size={18} />
              {t('reviews') || 'Reviews'}
            </button>
          </div>
          
          <div className="footer-right">
            <div className="footer-good-to-know">
              <h3>{t('goodToKnow')}</h3>
              <div className="policy-badge">{t('bookingPolicy')}</div>
              <p>{t('bookingPolicyText')}</p>
            </div>
            <Link to="/privacy" className="footer-privacy-box" data-testid="footer-privacy-link">
              <Shield size={14} />
              <span>{t('privacyPolicy') || 'Privacy Policy'}</span>
            </Link>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Registan Tourism. {t('allRightsReserved')}.</p>
        </div>
      </footer>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default BusinessPage;
